import React, { useMemo } from 'react';
import * as mobxReact from 'mobx-react';
import { IntlShape } from 'react-intl';
import { EventTrackerListItem } from '../_event-tracker-list-item/event-tracker-list-item';
import { EventTrackerVM } from '../../event-tracker.viewmodel';
import { EventStatus, IEntityRoleDTO, IEventTrackerDetailsDTO } from '../../../../api/events.dao';
import { EventTrackerListGroupHeader } from '../_event-tracker-list-group-header/event-tracker-list-group-header';

export interface IProps {
    colSpan: number;
    group: IEventTrackerGroup;
    searchQuery?: string;
    vm: EventTrackerVM;
    viewPlanningNotesAccess: boolean;
    canRenderAddMembersButton: boolean;
    intl: IntlShape;
    showColumn: (id) => boolean;
    withDateColumn: boolean;
    withTimeColumn: boolean;
    withTotalPresenterCombinationsByDate: boolean;
    wideTdName?: string;
    trackCancelDeliveryTeamButtonMixPanel?: () => void;
    deliveryMemberRoles: IEntityRoleDTO[];
    className?: string;
}

export interface IEventTrackerGroup {
    id: string;
    mergedDateTime: Date;
    meetings: IEventTrackerDetailsDTO[];
}

const EventTrackerListGroupItem = mobxReact.observer((props: IProps) => {
    const {
        group,
        colSpan,
        vm,
        intl,
        showColumn,
        deliveryMemberRoles,
        ...otherProps
    } = props;

    const {
        collapsedGroups,
        onCollapseGroup,
        onExpandGroup,
        isPlanningNotesFormShown,
        onPlanningNotesClick,
        fetchDeliveryTeamItems,
        setAssignedDeliveryTeam,
        removeDeliveryItem,
        setDeliveryMemberRole,
        fetchEventsList,
        trackCancelDeliveryTeamButtonMixPanel
    } = vm;

    const {
        mergedDateTime,
        meetings,
    } = group;

    const isGroupCollapsed = useMemo(() => collapsedGroups.some(el =>
        el.id === group.id
    ), [collapsedGroups]);

    const handleCollapseGroupToggle = () => {
        if (isGroupCollapsed) {
            onExpandGroup(group);
        } else {
            onCollapseGroup(group);
        }
    };

    const eventsCount = meetings.reduce(
        (acc, curr) => curr.status === EventStatus.IN_PROGRESS ? acc + 1 : acc, 0
    );

    return (
        <>
            <EventTrackerListGroupHeader
                mergedDateTime={mergedDateTime}
                colSpan={colSpan}
                onCollapseToggle={handleCollapseGroupToggle}
                isCollapsed={isGroupCollapsed}
                itemsLength={eventsCount}
                intl={intl}
            />
            {!isGroupCollapsed && meetings.map((meeting: IEventTrackerDetailsDTO, index: number) => {
                const composedId = `${meeting.accountId}_${meeting.eventId}`;

                return (
                    <EventTrackerListItem
                        isFirstInTimeGroup={index === 0}
                        composedId={composedId}
                        key={composedId}
                        isSelected={isPlanningNotesFormShown(composedId)}
                        onPlanningNotesClick={onPlanningNotesClick}
                        fetchDeliveryTeamItems={fetchDeliveryTeamItems}
                        setAssignedDeliveryTeam={setAssignedDeliveryTeam}
                        removeDeliveryItem={removeDeliveryItem}
                        setDeliveryMemberRole={setDeliveryMemberRole}
                        startEditEvent={() => vm.editEvent(meeting, composedId, intl)}
                        showColumn={id => showColumn(id)}
                        fetchEventsList={fetchEventsList}
                        trackCancelDeliveryTeamButtonMixPanel={trackCancelDeliveryTeamButtonMixPanel}
                        deliveryMemberRoles={deliveryMemberRoles}
                        {...otherProps}
                        {...meeting}
                    />
                );
            })}
        </>
    );
});

export default EventTrackerListGroupItem;
